/*
 * Minimal tool metadata lookup — ONLY the 7 fields needed by the
 * `seoMetaFor()` adapter in `lib/i18n.js`. Extracted from the full
 * `SEO_TOOLS` array in `lib/seo_tools.js` (which is 53 KB and full
 * of citations, related-tools, JSON-LD bodies that the homepage
 * never reads).
 *
 * Why this exists: `i18n.js` was eagerly importing `SEO_TOOLS` just
 * to look up `titleLong / h1 / metaDescription / intro / keywords`
 * for the tool-slug branch of `seoMetaFor()`. That pulled the full
 * 53 KB array into the main bundle even though the homepage never
 * touches it — only `/rank`, `/da-checker`, etc. routes (which are
 * already lazy-loaded) need the heavy fields.
 *
 * Net effect: ~50 KB shaved off `main.js`.
 */

export const TOOL_META_MIN = [
  {
    kind: "rank",
    slug: "rank",
    titleShort: "FeedPulse Traffic Rank",
    titleLong: "Free Website Rank Checker — Traffic Rank + Badge \u00b7 FeedPulse",
    h1: "Free website rank checker — traffic rank for any domain",
    metaDescription: "Find any site's global traffic rank free, with no signup \u2014 a free Similarweb and Alexa rank alternative, plus an embeddable rank badge for your own site.",
    intro: "Alexa.com shut down on May 1, 2022, and its free traffic rank went with it. FeedPulse Rank fills that gap: paste any domain to see where it sits in the Tranco list of the top one million sites, with a rough monthly-visits estimate and an embeddable rank badge for your own site. Free, no signup.",
    keywords: [
      "alexa rank alternative",
      "free website rank checker",
      "website rank checker",
      "free site rank tool",
      "similarweb alternative free",
      "semrush rank alternative",
      "ahrefs rank alternative",
      "moz rank alternative",
      "free site traffic rank",
      "global website rank",
      "website ranking widget",
      "traffic rank badge",
      "alexa rank checker 2026",
      "check website rank free",
      "domain traffic rank",
      "rank tracker free",
      "website popularity rank",
    ],
  },
  {
    kind: "da",
    slug: "da-checker",
    titleShort: "Domain Authority Checker",
    titleLong: "Free Domain Authority Checker \u2014 DA Score + Badge \u00b7 FeedPulse",
    h1: "Free Domain Authority checker \u00b7 DA badge widget",
    metaDescription: "Check any domain\u2019s authority score free \u2014 no signup, no ads, no card. A 0-100 score in two seconds, plus an embeddable badge for your own site.",
    intro: "Domain Authority is the SEO industry's most cited authority metric. This free DA checker returns a 0-100 score for any domain in about two seconds, with no ads, no popups and no signup \u2014 and every check comes with an embeddable DA badge for your own site. While Moz's free API has quota left we serve its official score; past that we serve a transparent OpenPageRank-derived estimate and label it as one, so you always know which number you are looking at.",
    keywords: [
      "free domain authority checker",
      "DA checker",
      "moz domain authority checker",
      "free DA tool",
      "domain authority badge",
      "smallseotools alternative",
      "website authority checker",
      "DA score checker",
      "bulk DA checker",
      "DA checker by Moz",
      "free seo tools",
      "domain authority widget",
      "check DA score free",
      "moz rank checker",
      "site authority badge",
      "DA PA checker free",
      "domain authority lookup",
    ],
  },
  {
    kind: "dr",
    slug: "dr-checker",
    titleShort: "Domain Rating Checker",
    titleLong: "Free DR Checker \u2014 Domain Rating Score + Badge \u00b7 FeedPulse",
    h1: "Free DR checker \u00b7 Domain Rating score + badge",
    metaDescription: "Check any domain's DR free \u2014 a 0-100 Domain Rating from open backlink data, no signup. An Ahrefs DR alternative, plus an embeddable DR badge for your site.",
    intro: "Ahrefs' own DR is free for one-off lookups on its website, but it comes without a badge you can embed. This free DR checker returns a 0-100 backlink-authority score for any domain in about two seconds, with no signup and no card, and every check comes with an embeddable DR badge for your own site. The number is computed from the open OpenPageRank dataset and rescaled into the 0-100 range you know from Ahrefs \u2014 it is an independent equivalent, not Ahrefs' own figure, and the page tells you so every time.",
    keywords: [
      "free domain rating checker",
      "free dr checker",
      "dr checker",
      "check dr free",
      "test ahrefs dr",
      "ahrefs domain rating",
      "ahrefs DR alternative",
      "DR checker free",
      "domain rating widget",
      "DR badge",
      "free DR tool",
      "domain rating lookup",
      "check DR score free",
      "ahrefs alternative free",
      "website DR score",
      "DR metric checker",
      "open page rank widget",
      "site rating badge",
    ],
  },
  {
    kind: "trust",
    slug: "trust-score",
    titleShort: "Website Trust Score Checker",
    titleLong: "Free Website Trust Score Checker + Badge \u00b7 FeedPulse",
    h1: "Free website trust score checker",
    metaDescription: "One 0-100 website trust score from seven public signals \u2014 global rank, domain age, HTTPS and the four Lighthouse scores. Free embeddable badge, no signup.",
    intro: "FeedPulse is a free website trust score checker: it blends seven public signals \u2014 global traffic rank, domain age, HTTPS, and Google's four Lighthouse scores \u2014 into a single 0-100 grade for any website. Every weight is published below, so you can check the arithmetic yourself. No signup, and there is an embeddable badge for your own site.",
    keywords: [
      "website trust score checker",
      "website trust score",
      "domain trust score",
      "site trust score",
      "trust score checker",
      "free website trust rating",
      "website trust badge",
      "trust badge embed",
      "trust rating widget",
      "domain trust rating",
      "free SEO metrics widget",
      "0-100 trust score",
    ],
  },
  {
    kind: "lighthouse",
    slug: "lighthouse",
    titleShort: "Lighthouse Score Checker",
    titleLong: "Free Lighthouse Checker + Score Badge \u00b7 FeedPulse",
    h1: "Free Lighthouse checker + auto-updating score badge",
    metaDescription: "Check any site's four Lighthouse scores from Google's PageSpeed Insights API, then embed them as an auto-updating badge. Free, no signup.",
    intro: "Paste a URL and get the four Lighthouse scores Google's PageSpeed Insights API returns \u2014 Performance, SEO, Accessibility and Best Practices \u2014 measured on mobile by default. Free, no signup, and you can embed the result as a badge that keeps itself up to date.",
    keywords: [
      "lighthouse checker",
      "lighthouse score checker",
      "google lighthouse online",
      "lighthouse score badge",
      "google lighthouse badge",
      "lighthouse widget",
      "pagespeed insights badge",
      "lighthouse score widget",
      "performance badge website",
      "accessibility score badge",
      "embed lighthouse score",
      "lighthouse badge wordpress",
      "lighthouse score embed",
    ],
  },
  {
    kind: "speed",
    slug: "speed",
    titleShort: "Page Speed Badge",
    titleLong: "Free Page Speed Test + Speed Badge · FeedPulse",
    h1: "Free page speed test + auto-updating speed badge",
    metaDescription: "Free page speed test for any URL via Google PageSpeed Insights. See mobile load speed and performance score, then embed an auto-updating badge. No signup.",
    intro: "Test how fast any page loads on mobile with Google's PageSpeed Insights API, then show the result on your own site as a 'This site loads in X ms' badge. Re-checked every 6 hours once your site is registered, every 14 days for one-off lookups. Free, no signup.",
    keywords: [
      "free page speed test",
      "website speed test free",
      "page speed test",
      "page speed badge",
      "site speed widget",
      "loads in ms badge",
      "page load time widget",
      "website speed checker",
      "load time badge",
      "free pagespeed widget",
      "website performance badge",
      "fast loading website badge",
      "site speed embed",
      "page speed insights widget",
      "free speed test badge",
    ],
  },
  {
    kind: "age",
    slug: "domain-age",
    titleShort: "Domain Age Checker",
    titleLong: "Free Domain Age Checker \u2014 WHOIS + Badge \u00b7 FeedPulse",
    h1: "Free Domain Age Checker + 'Online since' badge",
    metaDescription: "Check when any domain was registered — free, from public RDAP registry data, no signup. Includes an embeddable ‘Online since 2008’ badge for your own site.",
    intro: "How old is any website? A free domain age lookup from public RDAP registry data (the modern replacement for WHOIS), plus the earliest Wayback Machine snapshot as a second opinion. Comes with an 'Online since' badge for your own site. No signup, no ads.",
    keywords: [
      "domain age checker",
      "whois domain age",
      "online since badge",
      "website age widget",
      "free domain age lookup",
      "how old is this website",
      "domain registration date checker",
      "established badge website",
      "since YYYY badge",
      "domain creation date",
      "site age verification",
      "domain age tool",
      "established date badge",
    ],
  },
  {
    kind: "backlinks",
    slug: "backlinks",
    titleShort: "Free Backlink Checker",
    titleLong: "Free Backlink Checker \u2014 Inbound Links + Badge \u00b7 FeedPulse",
    h1: "Free backlink checker \u2014 count the links pointing at any site",
    metaDescription: "Count inbound links and referring domains for any site, free and without signup. Includes a self-updating \u2018X sites link here\u2019 badge for your own pages.",
    intro: "Count the backlinks pointing at any website. A free backlink checker and counter badge, re-read about once a month. No signup, built for quick counts and embeddable badges \u2014 it shows totals, not the list of linking pages you get in Ahrefs, Moz or Semrush.",
    keywords: [
      "free backlinks checker",
      "backlink checker",
      "backlink checker tool",
      "check your website backlinks",
      "backlinks counter",
      "free backlink checker tool",
      "ahrefs backlink checker alternative",
      "moz link explorer alternative",
      "semrush backlinks checker alternative",
      "free backlinks widget",
      "backlink counter",
      "inbound links checker",
      "referring domains checker",
      "backlinks badge embed",
      "free backlinks lookup",
    ],
  },
  {
    kind: "serp",
    slug: "serp-checker",
    titleShort: "Free SERP Checker",
    titleLong: "Free SERP Checker \u2014 Google Rank for 3 Keywords \u00b7 FeedPulse",
    h1: "Free SERP position checker \u2014 see where you rank on Google",
    metaDescription: "See exactly where your site ranks on Google for up to 3 keywords, in 30 countries, desktop or mobile. Results in seconds. No signup, no card.",
    intro: "Free SERP position checker \u2014 paste a URL, pick up to 3 keywords, and instantly see your Google ranking for each across 30 country domains on desktop or mobile. Scans the top 30 results (positions 1-30), the band where moving up still changes your traffic. The free Ahrefs Rank Tracker, Semrush Position Tracking, and SerpWatch alternative. Plus an embeddable 'Ranked #3 on Google for X' badge.",
    keywords: [
      "free serp checker",
      "google rank checker",
      "keyword position checker",
      "serp position checker",
      "free serp position tool",
      "google ranking checker",
      "ahrefs rank tracker alternative",
      "semrush position tracking alternative",
      "serpwatch alternative",
      "google keyword rank checker",
      "google serp tool free",
      "keyword ranking checker free",
      "google position tracker",
      "google search rank",
      "check google ranking free",
    ],
  },
  {
    kind: "indexed",
    slug: "indexed-checker",
    titleShort: "Google Index Checker",
    titleLong: "Free Google Index Checker \u2014 Is My Page Indexed? \u00b7 FeedPulse",
    h1: "Free Google index checker \u2014 is your page indexed?",
    metaDescription: "Check whether any URL is indexed by Google, free and without login. See the title and snippet Google shows for it, or check up to five URLs at once.",
    intro: "Free Google index checker: paste any URL and see whether it shows up in Google's index, with the title and snippet Google displays for it. Check one page or up to five at once, with no login or site verification needed.",
    keywords: [
      "google index checker",
      "is my page indexed by google",
      "free google index checker",
      "check if website is indexed",
      "google indexing checker",
      "page indexed checker",
      "google search console alternative",
      "url indexed by google",
      "check google index status",
      "free indexing checker",
      "site indexing checker",
      "google indexed pages checker",
      "bulk index checker free",
      "google index status",
      "url inspection alternative",
      "verify google indexing",
      "indexed pages google",
      "url indexed checker",
    ],
  },
];

// Build slug → entry lookup once at module load (constant time).
export const TOOL_META_BY_SLUG = Object.fromEntries(
  TOOL_META_MIN.map((t) => [t.slug, t]),
);
export const TOOL_SLUGS = new Set(Object.keys(TOOL_META_BY_SLUG));
